import * as React from 'react';
import _ from 'lodash';
import { withTranslation, WithTranslation } from 'react-i18next';

import styles from './index.module.css';
import defaultStyles from '../../../styles/formSetStyles.module.css';

import * as api from '../../../api';
import * as logger from '../../../logger';

import { Button } from '../../../components/Button';
import { GroupInputs } from '../../../components/GroupInputs';
import { InfoMessage } from '../../../components/InfoMessage';
import LabeledInput from '../../../components/LabeledInput';
import {
  LoadingSpinner,
  LoadingSpinnerSize,
} from '../../../components/LoadingSpinner';
import { SelectInput } from '../../../components/SelectInput';

///////////////////////////////////////////////////
//////TYPES

interface Props extends WithTranslation {
  navigateToNextPage: () => Promise<void>;
  setFormField: (formFieldId: string, value: string) => void;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  practicePreference: api.Previsit.PracticePreference;
  practiceGroupId?: string;
}

interface State {
  initialMedicationsCount?: number;
  isModalOpen: boolean;
  medications: api.Medication.PrevisitMedication[];
  errorMessage: string;
}

///////////////////////////////////////////////////
//////COMPONENT

class MedicationsPageComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const value = props.field.value;

    if (!value) {
      this.state = {
        initialMedicationsCount: 0,
        isModalOpen: false,
        medications: [],
        errorMessage: '',
      };
      return;
    }

    const parsed: api.Medication.PrevisitMedication[] = _.sortBy(
      JSON.parse(value) || [],
      'name',
    );

    this.state = {
      initialMedicationsCount: parsed.length,
      isModalOpen: false,
      medications: parsed.filter((medication) => !medication.isDeleted),
      errorMessage: '',
    };

    if (!this.props.field.value || this.props.field.value === '[]') {
      logger.error(
        new Error(
          `MedicationsPage.constructor patient should not see this component without ingested medications.`,
        ),
      );
    }
  }

  allMedicationsHaveStatus = () => {
    return !this.state.medications.some((medication) => !medication.status);
  };

  navigateToNextPage = async () => {
    if (!this.allMedicationsHaveStatus()) {
      this.setState({
        errorMessage: this.props.t(
          'Please select an option for each medication',
        ),
      });
      window.scrollTo(0, 0);
      return;
    }
    await this.props.navigateToNextPage();
  };

  updateFormField = async (
    medications: api.Medication.PrevisitMedication[],
  ) => {
    const { field, setFormField } = this.props;
    const { id, previsitFormSetId } = field;

    const newValue = JSON.stringify(medications);
    setFormField(id, newValue);
    try {
      await api.client.Previsit.updateFormField(
        previsitFormSetId,
        id,
        newValue,
      );
    } catch (err) {
      logger.error(err, 'Failed to update form field');
    }
  };

  updateMedicationStatus = async (
    medicationId: string,
    newStatus: api.Medication.Status,
  ) => {
    const { medications } = this.state;
    const foundIndex = medications.findIndex(
      (medication) => medication.id === medicationId,
    );

    medications[foundIndex] = {
      ...medications[foundIndex],
      status: newStatus,
    };

    if (this.allMedicationsHaveStatus()) {
      this.setState({
        errorMessage: '',
      });
    }

    this.setState({ medications });
    await this.updateFormField(medications);
  };

  render() {
    const { t } = this.props;
    const { medications, errorMessage } = this.state;
    if (!medications) {
      return (
        <div className={styles.LoadingWrapper}>
          <h2>{t('Loading...')}</h2>
          <div className={styles.LoadingSpinnerWrapper}>
            <LoadingSpinner size={LoadingSpinnerSize.Small} />
          </div>
        </div>
      );
    }
    const DefaultMedicationComplianceOptions = [
      api.Medication.Status.NotTaking,
      api.Medication.Status.TakingAsPrescribed,
      api.Medication.Status.TakingNotAsPrescribed,
    ];
    const medicationComplianceOptions = this.props.practicePreference
      .previsitMedicationComplianceOptions.length
      ? this.props.practicePreference.previsitMedicationComplianceOptions
      : DefaultMedicationComplianceOptions;
    const statusMap =
      this.props.practiceGroupId &&
      api.Medication.PRACTICE_GROUP_IDS_WITH_STATUS_MAP_EXCEPTIONS.includes(
        this.props.practiceGroupId,
      )
        ? {
            ...api.Medication.statusMap,
            [api.Medication.Status.TakingAsPrescribed]: 'Taking as directed',
            [api.Medication.Status.TakingNotAsPrescribed]:
              'Taking but not as directed',
          }
        : api.Medication.statusMap;

    return (
      <div className={styles.MedicationsPage}>
        {errorMessage ? (
          <div className={styles.InfoMessage}>
            <InfoMessage isError={true}>{errorMessage}</InfoMessage>
          </div>
        ) : null}
        <label className={defaultStyles.Label}>
          {t('Are you taking the following medications?')}
        </label>
        <div className={styles.Medications}>
          {medications.map((medication) => (
            <GroupInputs key={medication.id} classNames={[styles.GroupInputs]}>
              <LabeledInput
                label={medication.name}
                subLabel={medication.detailsRaw || ''}
                uppercaseLabel={true}
              >
                <SelectInput
                  value={medication.status}
                  onChange={(newValue) =>
                    this.updateMedicationStatus(medication.id, newValue)
                  }
                  options={medicationComplianceOptions.map((v) => ({
                    display: t(statusMap[v]),
                    key: v,
                    value: v,
                  }))}
                />
              </LabeledInput>
            </GroupInputs>
          ))}
        </div>
        <div className={defaultStyles.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={this.props.t('Continue')}
            onClick={this.navigateToNextPage}
          />
        </div>
      </div>
    );
  }
}

export default withTranslation()(MedicationsPageComponent);
